import { useEffect, useState } from 'react';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, Check, X, Trash2 } from 'lucide-react';

export default function LeadShield() {
  const [leads, setLeads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', source: 'web', inquiry: '', property_interest: '' });

  const load = async () => {
    try {
      const { data } = await http.get('/leads');
      setLeads(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await http.post('/leads', form);
      setLeads([data, ...leads]);
      setForm({ name: '', email: '', phone: '', source: 'web', inquiry: '', property_interest: '' });
      setShowForm(false);
      toast.success('Lead captured.');
    } catch (e) { toast.error(formatError(e)); }
  };

  const qualify = async (l) => {
    setBusyId(l.id);
    try {
      const { data } = await http.post(`/leads/${l.id}/qualify`);
      setLeads(leads.map(x => x.id === l.id ? { ...x, ai_reply: data.ai_reply, qualified: true } : x));
      toast.success('AI reply drafted.');
    } catch (e) { toast.error(formatError(e)); }
    setBusyId(null);
  };

  const remove = async (id) => {
    try {
      await http.delete(`/leads/${id}`);
      setLeads(leads.filter(x => x.id !== id));
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-8">
      <header className="hq-fade-in">
        <p className="hq-eyebrow">Lead Shield</p>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="leadshield-title">Qualified. Pre-drafted.</h1>
        <div className="hq-divider-gold w-fit"></div>
        <p className="mt-4 max-w-2xl" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
          Add or import your leads — from a form, an email, a referral, or a DM. HomiHQ scores each one
          and drafts a reply in your voice. You review and send. Native email, form, and CRM sync are
          on the roadmap.
        </p>
      </header>

      <div className="flex justify-end">
        <button className="hq-btn-primary" onClick={() => setShowForm(!showForm)} data-testid="new-lead-btn">
          {showForm ? 'Cancel' : '+ New Lead'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="hq-card p-8 grid grid-cols-1 md:grid-cols-2 gap-5 hq-fade-in">
          <div><label className="hq-label">Name</label><input className="hq-input" required data-testid="lead-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
          <div><label className="hq-label">Source</label><input className="hq-input" value={form.source} onChange={e => setForm({...form, source: e.target.value})} placeholder="Zillow, referral, Instagram…" /></div>
          <div><label className="hq-label">Email</label><input className="hq-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
          <div><label className="hq-label">Phone</label><input className="hq-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
          <div className="md:col-span-2"><label className="hq-label">Property Interest</label><input className="hq-input" value={form.property_interest} onChange={e => setForm({...form, property_interest: e.target.value})} placeholder="e.g. 4BR craftsman under $1.4M in Beacon Hill" /></div>
          <div className="md:col-span-2"><label className="hq-label">Inquiry</label><textarea className="hq-input" rows={3} required data-testid="lead-inquiry-input" value={form.inquiry} onChange={e => setForm({...form, inquiry: e.target.value})} /></div>
          <div className="md:col-span-2 text-right">
            <button className="hq-btn-primary" data-testid="save-lead-btn">Capture Lead</button>
          </div>
        </form>
      )}

      <div className="space-y-4" data-testid="leads-list">
        {leads.length === 0 && (
          <div className="hq-card p-16 text-center" style={{ color: 'var(--muted)' }}>
            <div className="hq-eyebrow mb-2">Inbox clear</div>
            <div className="font-serif text-2xl" style={{ color: 'var(--sage)' }}>
              No leads yet. When they arrive, they'll be qualified before you see them.
            </div>
          </div>
        )}

        {leads.map(l => (
          <div key={l.id} className="hq-card p-8 hq-fade-in" data-testid={`lead-card-${l.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="font-serif text-2xl" style={{ color: 'var(--sage)' }}>{l.name}</h3>
                  {l.qualified && (
                    <span className="hq-eyebrow px-2 py-1" style={{ background: 'var(--gold)', color: '#fff', borderRadius: 2 }}>
                      <Check size={10} className="inline mr-1" /> Qualified
                    </span>
                  )}
                </div>
                <div className="text-xs" style={{ color: 'var(--muted)' }}>
                  {l.source} · {l.email || 'no email'} · {l.phone || 'no phone'}
                </div>
              </div>
              <button onClick={() => remove(l.id)} data-testid={`delete-lead-${l.id}`}>
                <Trash2 size={14} style={{ color: 'var(--muted)' }} />
              </button>
            </div>

            <div className="mb-4 text-sm" style={{ color: 'var(--ink-soft)', lineHeight: 1.65 }}>
              {l.property_interest && <div className="mb-2"><span className="hq-eyebrow">Interest</span><div>{l.property_interest}</div></div>}
              <div className="hq-eyebrow mt-2">Inquiry</div>
              <div className="italic">"{l.inquiry}"</div>
            </div>

            {l.ai_reply ? (
              <div className="p-5 mt-4" style={{ background: 'var(--parchment)', border: '1px solid var(--gold)', borderRadius: 2 }}>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={14} style={{ color: 'var(--gold)' }} />
                  <span className="hq-eyebrow" style={{ color: 'var(--gold-soft)' }}>AI Reply Draft</span>
                </div>
                <pre className="whitespace-pre-wrap font-serif text-base" style={{ color: 'var(--ink)', lineHeight: 1.7 }} data-testid={`ai-reply-${l.id}`}>
                  {l.ai_reply}
                </pre>
              </div>
            ) : (
              <button
                onClick={() => qualify(l)}
                disabled={busyId === l.id}
                className="hq-btn-gold"
                data-testid={`qualify-lead-${l.id}`}
              >
                {busyId === l.id ? 'AI thinking…' : <><Sparkles size={12} className="inline mr-1"/> Qualify & Draft Reply</>}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
